import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bed, House } from "@phosphor-icons/react";
import { roomImage } from "@/lib/roomImages";
import FormStatusIcons from "./FormStatusIcons";
import FormSubmissionModal from "./FormSubmissionModal";

/**
 * Aktuelle Belegung — pro Zimmer:
 *  - belegtes Zimmer: roter Rahmen · Gastname · „bis TT.MM."
 *  - freies Zimmer:   grüner Rahmen · „Frei"
 *  - Nächster Gast: TT.MM. (falls Folgebuchung vorhanden)
 *  - Form-Status-Icons (Check-in / Frühstück / Abendessen) für die aktive Buchung
 */
const fmtDate = (iso) => {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return `${d}.${m}.`;
};

export default function CurrentOccupancy() {
  const nav = useNavigate();
  const [rooms, setRooms] = useState(null);
  const [status, setStatus] = useState({});
  const [modal, setModal] = useState(null); // {bookingId, formType}

  const loadStatus = async (bookingIds) => {
    if (!bookingIds.length) return;
    try {
      const { data } = await api.get("/forms/bookings-status", { params: { ids: bookingIds.join(",") } });
      setStatus(data);
    } catch (_) {}
  };
  const markManual = async (bookingId, form_type, done) => {
    try {
      await api.post(`/bookings/${bookingId}/manual-form-mark`, { form_type, done });
      setStatus(prev => ({
        ...prev,
        [bookingId]: { ...(prev[bookingId] || {}), [`manual_${form_type}`]: done }
      }));
    } catch (_) {
      window.alert("Konnte Markierung nicht speichern. Bitte erneut versuchen.");
    }
  };

  useEffect(() => {
    api.get("/rooms").then(r => {
      setRooms(r.data);
      const bids = r.data.map(x => x.current_booking?.id).filter(Boolean);
      loadStatus(bids);
    }).catch(() => setRooms([]));
  }, []);

  if (rooms === null) return null;

  return (
    <section className="msb-card" data-testid="current-occupancy">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-2xl bg-primary flex items-center justify-center">
          <House size={20} weight="duotone" color="white"/>
        </div>
        <div>
          <h2 className="text-xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Aktuelle Belegung</h2>
          <p className="text-xs text-muted-foreground">Klick auf die Karte öffnet das Journal · Klick auf ein farbiges Icon zeigt die Formular-Einsendung.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {rooms.map((r) => {
          const busy   = !!r.current_booking && !r.current_booking.is_block;
          const nextCi = r.next_booking?.check_in;
          const cardCls = busy
            ? "border-2 border-red-600 bg-red-50/40 hover:bg-red-50"
            : "border-2 border-emerald-600 bg-emerald-50/30 hover:bg-emerald-50";
          const bookingId = r.current_booking?.id;
          const st = bookingId ? status[bookingId] : null;

          return (
            <div
              key={r.id}
              className={`relative rounded-xl p-4 transition shadow-sm ${cardCls}`}
              data-testid={`occupancy-room-${r.id}`}
            >
              {/* Rundes Vorschaubild oben rechts */}
              <img
                src={roomImage(r.name)}
                alt={r.name}
                className="absolute top-3 right-3 w-14 h-14 rounded-full object-cover ring-2 ring-white shadow-sm opacity-95 pointer-events-none"
                loading="lazy"
                data-testid={`occupancy-room-thumb-${r.id}`}
              />
              <button
                type="button"
                onClick={() => nav(`/monatsjournal?room=${encodeURIComponent(r.id)}`)}
                className="w-full text-left focus:outline-none focus:ring-2 focus:ring-primary rounded-lg pr-16"
                title={`Buchungen für ${r.name} ansehen`}>

                {/* Zimmername */}
                <div className="flex items-center gap-2">
                  <Bed size={16} weight="duotone" className={busy ? "text-red-700" : "text-emerald-700"}/>
                  <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
                    {r.name}
                  </div>
                </div>

                {/* Aktueller Gast oder „Frei" */}
                <div className="mt-2">
                  {busy ? (
                    <>
                      <div className="font-bold text-base leading-tight"
                           style={{fontFamily:"'Work Sans',sans-serif"}}>
                        {r.current_guest_name || "Gast"}
                      </div>
                      <div className="text-sm text-red-700 font-semibold mt-0.5">
                        bis {fmtDate(r.current_booking.check_out)}
                      </div>
                      {(r.current_breakfast > 0 || r.current_dinner > 0) && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`occ-meals-current-${r.id}`}>
                          {r.current_breakfast > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold ring-1 ring-amber-200">
                              🥐 {r.current_breakfast}× Frühstück
                            </span>
                          )}
                          {r.current_dinner > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 text-[11px] font-semibold ring-1 ring-indigo-200">
                              🍽️ {r.current_dinner}× Abendessen
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="font-bold text-lg text-emerald-700 leading-tight tracking-wide"
                         style={{fontFamily:"'Work Sans',sans-serif"}}>
                      Frei
                    </div>
                  )}
                </div>
              </button>

              {/* Formular-Status */}
              {busy && bookingId && (
                <div className="mt-2 pt-2 border-t border-dashed border-muted-foreground/30 flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">Formulare</span>
                  <FormStatusIcons
                    status={st}
                    onOpen={(ft) => setModal({ bookingId, formType: ft })}
                    onManualToggle={(ft, done) => markManual(bookingId, ft, done)}
                    testidPrefix={`occ-form-${r.id}`}
                  />
                </div>
              )}

              {/* Nächster Gast */}
              <div className="mt-2 pt-2 border-t border-dashed border-muted-foreground/30 text-[11px] text-muted-foreground">
                {nextCi
                  ? (
                    <>
                      <div>
                        Nächster Gast: <span className="font-semibold text-foreground">{fmtDate(nextCi)}</span>
                        {r.next_nights ? <> · <span className="font-semibold text-foreground">{r.next_nights} {r.next_nights === 1 ? "Nacht" : "Nächte"}</span></> : null}
                        {r.next_guest_name ? <> · <span className="font-semibold text-foreground">{r.next_guest_name}</span></> : null}
                      </div>
                      {(r.next_breakfast > 0 || r.next_dinner > 0) && (
                        <div className="flex flex-wrap gap-1.5 mt-1" data-testid={`occ-meals-next-${r.id}`}>
                          {r.next_breakfast > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold ring-1 ring-amber-200">
                              🥐 {r.next_breakfast}× Frühstück
                            </span>
                          )}
                          {r.next_dinner > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 text-[11px] font-semibold ring-1 ring-indigo-200">
                              🍽️ {r.next_dinner}× Abendessen
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  )
                  : <span className="italic">Keine Folgebuchung geplant</span>}
              </div>
            </div>
          );
        })}
      </div>

      <FormSubmissionModal
        bookingId={modal?.bookingId}
        formType={modal?.formType}
        open={modal !== null}
        onClose={() => setModal(null)}
      />
    </section>
  );
}
